import { FormValues } from '@ynap/form';
import { normalizeStringHelper } from '@ynap/normalize-string-helper';
import {
    Locale,
    Field,
    Province,
    FieldType,
    Country,
    CountryInfo,
    CountryOptionItem,
    RetrieveLoqateResponse,
    RetrieveItemsEntity,
    Messages,
    Message,
    MappedSelectedAddressField,
    RetrieveItemsMap,
} from '../@types';

export interface WcsMapType {
    [key: string]: FieldType;
}

export const snakeToCamel = (str: string) => str.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));

export const getAccessor = (target: string, index: number, extraField?: string) => {
    return (values?: FormValues) => {
        let value = values?.[target];
        value = Array.isArray(value) ? value[index] : value;
        value = extraField ? value?.[extraField] : value;
        return value;
    };
};

export const toWCSMapping = (type: string): FieldType => {
    const mappings: WcsMapType = {
        COUNTRY: { name: 'country' },
        PERSON_TITLE: { name: 'personTitle' },
        FIRST_NAME: { name: 'firstName' },
        LAST_NAME: { name: 'lastName' },
        ADDRESS_LINE_1: { name: 'addressLine[0]', accessor: getAccessor('addressLine', 0) },
        ADDRESS_LINE_2: { name: 'addressLine[1]', accessor: getAccessor('addressLine', 1) },
        PRONUNCIATION_FIRST_NAME: { name: 'pronunciationFirstName', accessor: getAccessor('pronunciations', 0, 'firstName') },
        PRONUNCIATION_LAST_NAME: { name: 'pronunciationLastName', accessor: getAccessor('pronunciations', 0, 'lastName') },
        CITY: { name: 'city' },
        PROVINCE: { name: 'state' },
        ZIP: { name: 'zipCode' },
        PHONE: { name: 'phone1' },
        ALTERNATE_PHONE: { name: 'phone2' },
        PCCC: { name: 'PCCC' },
    };

    return (
        mappings[type] || {
            name: snakeToCamel(type),
        }
    );
};

export const getMappedCountryData = (countryData: Country, countries: Array<CountryInfo>) => ({
    [countryData.code]: {
        ...countryData,
        fields: countryData.fields.map((field: Field) => {
            if (field.type == 'PERSON_TITLE') {
                return {
                    ...field,
                    ...{ values: field.values ? field.values.map((value) => (typeof value === 'string' ? { display: value, value } : value)) : [] },
                };
            } else if (field.type == 'COUNTRY') {
                return {
                    ...field,
                    ...{ values: countries.map(({ countryName: display, countryCode: value }: CountryInfo) => ({ display, value })) },
                };
            } else if (
                countryData.dataType &&
                countryData.data &&
                countryData.data.length &&
                field.type == countryData.dataType &&
                (!field.values || field.values.length == 0)
            ) {
                return {
                    ...field,
                    ...{ values: countryData.data.map(({ name, code }: Province) => ({ display: name, value: code })) },
                };
            }
            return field;
        }),
    },
});

export const mapAddressValues = (values: FormValues, locale: Locale) => {
    const { pronunciationFirstName, pronunciationLastName } = values;
    if (pronunciationFirstName) {
        return {
            ...values,
            pronunciations: [
                {
                    firstName: pronunciationFirstName,
                    lastName: pronunciationLastName,
                    locale: locale.locale,
                },
            ],
        };
    }
    return values;
};

export const getSearchedCountries = (searchValue: string, countries: Array<CountryOptionItem>) => {
    /*
   1. Search should be by full country name as well as by country code, e.g. 'gb' should return 'United Kingdom'.
   2. Search should perform 'starts with' check (not 'contains'), e.g. 'str' should not return 'Austria'.
   3. For multi-word country names search should check all words, e.g. 'sta' should return 'United States'.
   4. Search term should not be highlighted in the search results.
   5. Search should be case-insensitive, e.g. 'uNi' should return 'United Kingdom'.
   6. Search should ignore leading and trailing whitespaces in the search term, e.g. '(space)uni(space)' should still return 'United Kingdom'.
  */
    const searchValueLower = searchValue ? normalizeStringHelper(searchValue.toLowerCase().trim()) : normalizeStringHelper(searchValue);
    return searchValueLower
        ? countries.filter(({ value: countryCode, labelNative: countryName }) => {
              const countryCodeLower = countryCode.toLowerCase();
              const countryNameLower = normalizeStringHelper(countryName.toLowerCase());
              const countryNameLowerWords = countryNameLower.split(' ');

              return (
                  countryCodeLower.startsWith(searchValueLower) ||
                  countryNameLower.startsWith(searchValueLower) ||
                  countryNameLowerWords.some((countryNameLowerWord: any) => normalizeStringHelper(countryNameLowerWord).startsWith(searchValueLower))
              );
          })
        : [];
};

export const retrieveItemsMap: RetrieveItemsMap = {
    Line1: 'Field1',
    Line2: 'Field2',
    Line3: 'Field3',
    City: 'Field4',
    ProvinceName: 'Field5',
    ProvinceCode: 'Field6',
    AdminAreaName: 'Field7',
    AdminAreaCode: 'Field8',
    District: 'Field9',
    PostalCode: 'Field10',
};

// builds query parameters so that the Loqate address fields are retrieved with only latin characters
export const buildFormattingQuery = () => {
    return Object.entries(retrieveItemsMap).reduce((acc, [key, value]) => {
        acc += `&${value}Format={${key}$}`;
        return acc;
    }, '');
};

export const getMappedSelectedAddress = (response: RetrieveLoqateResponse, addressFieldsMaxChars: number[] = [100, 100]) => {
    const engResponse: RetrieveItemsEntity[] = response.Items?.filter((res: RetrieveItemsEntity) => res.Language === 'ENG');
    const data = engResponse.length > 0 ? engResponse : response.Items;

    const mappedFields = Object.entries(retrieveItemsMap).reduce((acc, [key, value]) => {
        acc[key as keyof RetrieveItemsMap] = data[0][value as keyof RetrieveItemsEntity];
        return acc;
    }, {} as RetrieveItemsMap);

    const { City, Line1, Line2, Line3, ProvinceName, ProvinceCode, AdminAreaName, AdminAreaCode, District, PostalCode } = mappedFields;

    const { firstLine, secondLine } = constructAddressLines(Line1, Line2, Line3, addressFieldsMaxChars);

    const mappedAddress = [
        {
            field: 'addressLine[0]',
            name: firstLine || '',
        },
        {
            field: 'addressLine[1]',
            name: secondLine || '',
        },
        {
            field: 'city',
            name: City || '',
        },
        {
            field: 'state',
            name: ProvinceName,
            code: ProvinceCode,
            adminAreaName: AdminAreaName,
            adminAreaCode: AdminAreaCode,
            district: District,
        },
        {
            field: 'zipCode',
            name: PostalCode || '',
        },
    ];

    return mappedAddress;
};

// Used to fit 3 address lines into 2 input fields as best as possible considering validation constraints
const constructAddressLines = (Line1: string, Line2: string, Line3: string, addressFieldsMaxChars: number[]) => {
    const line1Limit = addressFieldsMaxChars[0];
    const line2Limit = addressFieldsMaxChars[1];

    let firstLine;
    let secondLine;

    const l1l2 = `${Line1}${Line2 != '' ? `, ${Line2}` : ''}`;
    const l2l3 = Line2 ? `${Line2}${Line3 != '' ? `, ${Line3}` : ''}` : '';
    if (l1l2.length > line1Limit) {
        firstLine = Line1;
        secondLine = l2l3.length > line2Limit ? Line2 : l2l3;
    } else {
        firstLine = l1l2;
        secondLine = Line3;
    }

    return { firstLine, secondLine };
};

const isMessages = (val: unknown): val is Messages => typeof val == 'object';

const isMessage = (val: unknown): val is Message => typeof val == 'function';

export const getMessageByKey = (messages?: Messages, key?: string): string => {
    if (isMessages(messages) && key) {
        const message = messages[key];
        if (isMessage(message)) return message();
    }
    return '';
};

export const findValue = (field: Field, selectedField: MappedSelectedAddressField) => {
    const { name, code, adminAreaName, adminAreaCode, district } = selectedField || {};
    const selectedValues = [name, code, adminAreaName, adminAreaCode, district].map((value) => value?.toLowerCase());

    if (!field.values) return name || undefined;

    for (const item of field.values) {
        const currentValue = typeof item !== 'string' ? item?.value : item;
        if (selectedValues.includes(currentValue?.toLowerCase())) {
            return currentValue;
        }
    }

    return undefined;
};
